import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { CalendarDays, LogOut, Ticket, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { BrandHeader } from "@/components/layouts/BrandHeader";

/**
 * Mobile-first PWA shell for the attendee (consumer) journey.
 * Bottom nav on mobile, hidden on desktop (replaced by header links).
 *
 * NOTE: this shell assumes routes /app and /app/wallet exist. Routes that are
 * persona-specific (e.g. /app/profile for partner) won't show in this nav.
 */
export function AttendeeShell() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { user, logout } = useAuth();

  const tabs = [
    { to: "/app", label: "Home", icon: Ticket, exact: true },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, exact: false },
    { to: "/app/wallet", label: "Wallet", icon: Wallet, exact: false },
  ] as const;

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <BrandHeader>
        <div className="flex items-center gap-3 sm:gap-4">
          <nav className="hidden items-center gap-4 sm:flex">
            {tabs.map((t) => {
              const active = t.exact ? path === t.to : path.startsWith(t.to);
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  className={cn(
                    "text-xs font-medium",
                    active ? "text-cream" : "text-[#e3c071]/80 hover:text-cream"
                  )}
                >
                  {t.label}
                </Link>
              );
            })}
          </nav>
          <div className="hidden items-center gap-1 text-xs text-[#e3c071]/80 sm:flex">
            <Ticket className="size-4" /> Attendee
          </div>
          {user?.email && (
            <div className="hidden max-w-[160px] truncate text-xs text-[#e3c071]/80 md:block">
              {user.email}
            </div>
          )}
          <Button
            onClick={() => void logout()}
            variant="ghost"
            size="sm"
            className="gap-1 px-2 text-cream hover:text-white"
            aria-label="Sign out"
          >
            <LogOut className="size-4" />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </BrandHeader>

      <main className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur md:hidden">
        <div className="container flex h-16 items-center justify-around">
          {tabs.map((t) => {
            const active = t.exact ? path === t.to : path.startsWith(t.to);
            return (
              <Link
                key={t.to}
                to={t.to}
                className={cn(
                  "flex flex-col items-center gap-1 text-[11px] font-medium",
                  active ? "text-palm" : "text-muted-foreground"
                )}
              >
                <t.icon className="size-5" />
                {t.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
